import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import SelectDatePicker from "components/dateRange";
import { useStoresSummaryStoresOnly } from "features/Stores";
import type { StoreAuditSummaryRangeDayModel } from "models";
import { auditDashboardTheme } from "../../theme";
import { useDateFilter } from "../../../../hooks/useFilterContext";
import AuditDailyAuditGrid from "./AuditDailyAuditGrid";
import RangeSummaryCard from "./RangeSummaryCard";
import {
  useAuditDashboardStoreAuditSummaries,
  useAuditDashboardStoreBackstockLedger,
  useAuditDashboardStoreScratcherReconciliationAuditsRange,
  useAuditDashboardManualSoldOutReports,
  useAuditDashboardManualSoldOutImpacts,
} from "../../hooks";
import { buildAuditListCards, formatAuditListRangeLabel } from "../../utils";
import { useAuditsCompleteThroughDate } from "features/audits/hooks/queries/useAuditsCompleteThroughDate";
import {
  buildAuditListFilterOptions,
  filterAuditListDays,
} from "../../utils/auditListFilters";
import type { AuditListFilterKey } from "../../utils/auditListFilters";
import { getNextAuditListReportDateSelection } from "../../utils/auditListSelection";

interface AuditListViewProps {
  selectedReportDate: string | null;
  onSelectReportDate: (reportDate: string) => void;
  onDateOrderChange?: (orderedReportDates: string[]) => void;
}

export default function AuditListView({
  selectedReportDate,
  onSelectReportDate,
  onDateOrderChange,
}: AuditListViewProps) {
  const { id: storeId } = useParams<{ id: string }>();
  const { startDate, endDate, formattedDateRange } = useDateFilter();
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [activeAuditFilter, setActiveAuditFilter] = useState<AuditListFilterKey>("all");
  const [visibleManualSoldOutDates, setVisibleManualSoldOutDates] = useState<string[]>([]);
  const filterFormMethods = useForm<{ auditListFilter: AuditListFilterKey }>({
    defaultValues: {
      auditListFilter: "all",
    },
  });

  const auditSummaryQuery = useAuditDashboardStoreAuditSummaries(
    storeId && startDate && endDate ? { storeId, startDate, endDate } : null
  );
  const backstockLedgerQuery = useAuditDashboardStoreBackstockLedger(
    storeId && startDate && endDate ? { storeId, startDate, endDate } : null
  );
  const scratcherAuditWindowsQuery = useAuditDashboardStoreScratcherReconciliationAuditsRange(
    storeId,
    startDate,
    endDate
  );
  const { data: allStores = [] } = useStoresSummaryStoresOnly(undefined, {
    enabled: Boolean(storeId),
  });
  const auditsCompleteThroughDateQuery = useAuditsCompleteThroughDate(storeId);
  const effectiveDate = auditsCompleteThroughDateQuery.data?.data?.effective_date as string | undefined;
  const storeDetails = useMemo(() => {
    if (!storeId) return undefined;
    return allStores.find((item) => item.store.id === storeId)?.store;
  }, [allStores, storeId]);
  const hasPosType = Boolean(storeDetails?.pos_type && storeDetails.pos_type !== "n/a");
  const isLottoAuthorized = Boolean(storeDetails?.lotto_authorized);

  const auditListCards = useMemo(() => {
    if (!auditSummaryQuery.data) {
      return [];
    }

    const cards = buildAuditListCards(
      auditSummaryQuery.data,
      backstockLedgerQuery.data ?? null,
      effectiveDate,
      scratcherAuditWindowsQuery.data ?? null,
      { hasPosType, isLottoAuthorized }
    );
    const summaryCard = cards.find((card) => card.variant === "summary");
    const dateCards = cards.filter((card) => card.variant === "date");

    const sortedDateCards = [...dateCards].sort((a, b) => {
      const comparison = a.id.localeCompare(b.id);
      return sortDirection === "asc" ? comparison : -comparison;
    });

    return [
      ...(summaryCard ? [summaryCard] : []),
      ...sortedDateCards,
    ];
  }, [auditSummaryQuery.data, backstockLedgerQuery.data, effectiveDate, scratcherAuditWindowsQuery.data, sortDirection, hasPosType, isLottoAuthorized]);

  const summaryCard = useMemo(
    () => auditListCards.find((card) => card.variant === "summary"),
    [auditListCards]
  );
  const dateCards = useMemo(
    () => auditListCards.filter((card) => card.variant === "date"),
    [auditListCards]
  );
  const dailySummariesByDate = useMemo(() => {
    const dayMap = new Map<string, StoreAuditSummaryRangeDayModel>();
    auditSummaryQuery.data?.dailySummaries.forEach((day) => {
      dayMap.set(day.reportDate, day);
    });
    return dayMap;
  }, [auditSummaryQuery.data]);
  const dailyAuditRows = useMemo(
    () =>
      dateCards
        .map((card) => dailySummariesByDate.get(card.id))
        .filter((day): day is StoreAuditSummaryRangeDayModel => Boolean(day)),
    [dailySummariesByDate, dateCards]
  );
  const auditFilterOptions = useMemo(
    () => buildAuditListFilterOptions(dailyAuditRows, { hasPosType, isLottoAuthorized }),
    [dailyAuditRows, hasPosType, isLottoAuthorized]
  );
  const visibleDailyAuditRows = useMemo(
    () => filterAuditListDays(dailyAuditRows, activeAuditFilter, { hasPosType, isLottoAuthorized }),
    [activeAuditFilter, dailyAuditRows, hasPosType, isLottoAuthorized]
  );
  const manualSoldOutReportsByDate = useAuditDashboardManualSoldOutReports(
    storeId,
    visibleManualSoldOutDates
  );
  const manualSoldOutImpactsByDate = useAuditDashboardManualSoldOutImpacts(
    storeId,
    visibleManualSoldOutDates
  );
  const handleVisibleReportDatesChange = useCallback((reportDates: string[]) => {
    setVisibleManualSoldOutDates(reportDates);
  }, []);
  const hasSummaryCard = Boolean(summaryCard);

  useEffect(() => {
    const filterExists = auditFilterOptions.some((item) => item.value === activeAuditFilter);
    if (!filterExists) {
      setActiveAuditFilter("all");
      filterFormMethods.setValue("auditListFilter", "all");
    }
  }, [activeAuditFilter, auditFilterOptions, filterFormMethods]);

  useEffect(() => {
    if (!onDateOrderChange) {
      return;
    }

    onDateOrderChange(visibleDailyAuditRows.map((day) => day.reportDate));
  }, [visibleDailyAuditRows, onDateOrderChange]);

  const headerDateRange =
    startDate && endDate
      ? formatAuditListRangeLabel(startDate, endDate)
      : auditSummaryQuery.data
        ? formatAuditListRangeLabel(auditSummaryQuery.data.startDate, auditSummaryQuery.data.endDate)
        : formattedDateRange;
  React.useEffect(() => {
    const canSelectSummary = hasSummaryCard && activeAuditFilter === "all";
    const nextReportDate = getNextAuditListReportDateSelection({
      selectedReportDate,
      visibleDailyAuditRows,
      canSelectSummary,
      storeTimezone: auditSummaryQuery.data?.storeTimezone ?? storeDetails?.timezone ?? null,
    });

    if (nextReportDate) {
      onSelectReportDate(nextReportDate);
    }
  }, [
    activeAuditFilter,
    auditSummaryQuery.data?.storeTimezone,
    hasSummaryCard,
    onSelectReportDate,
    selectedReportDate,
    storeDetails?.timezone,
    visibleDailyAuditRows,
  ]);

  return (
    <Box
      sx={{
        position: { lg: "sticky" },
        top: { lg: `${auditDashboardTheme.layout.stickyTop.md}px` },
        alignSelf: "start",
        height: { xs: "min(70vh, 640px)", lg: `calc(100vh - ${auditDashboardTheme.layout.stickyHeightOffset.md}px)` },
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          border: "1px solid",
          borderColor: auditDashboardTheme.colors.line,
          borderRadius: `${auditDashboardTheme.shape.containerRadius}px`,
          boxShadow: auditDashboardTheme.colors.shadow,
          overflow: "hidden",
          bgcolor: "#F1F5FA",
        }}
      >
        <Box
          sx={{
            px: "16px",
            py: "12px",
            borderBottom: "1px solid",
            borderColor: auditDashboardTheme.colors.containerBorder,
            bgcolor: "#f0f2f2",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) auto",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <Box sx={{ minWidth: 0, display: "grid", gap: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  component="h2"
                  sx={{
                    m: 0,
                    color: auditDashboardTheme.colors.text,
                    fontSize: "15px",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    textTransform: "uppercase",
                    letterSpacing: "0.3px",
                  }}
                >
                  Audit Dates
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: "2px" }}>
                <Typography
                  component="p"
                  sx={{
                    m: 0,
                    color: auditDashboardTheme.colors.text,
                    fontSize: "14px",
                    fontWeight: 500,
                    lineHeight: 1.2,
                  }}
                >
                  {headerDateRange}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                flexShrink: 0,
                ml: "auto",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flexWrap: "nowrap",
                justifyContent: "flex-end",
                minWidth: 0,
                "& .date-filter-input-container": {
                  display: "flex",
                  alignItems: "center",
                  boxSizing: "border-box",
                  minHeight: "32px !important",
                  height: "32px !important",
                  px: "10px !important",
                  py: "0 !important",
                  gap: 0.5,
                  width: 176,
                  maxWidth: 176,
                },
                "& .date-filter-input": {
                  flex: 1,
                  minWidth: 0,
                  m: 0,
                  fontSize: 12,
                  lineHeight: 1.1,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },
                "& .date-filter-input-container img": {
                  flexShrink: 0,
                  width: "18px !important",
                  height: "18px !important",
                },
              }}
            >
              <SelectDatePicker />
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            p: 0,
            minHeight: 0,
            scrollbarWidth: "thin",
            scrollbarColor: "rgba(163, 174, 208, 0.42) transparent",
            "&::-webkit-scrollbar": {
              width: 8,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(163, 174, 208, 0.42)",
              borderRadius: 999,
              border: "2px solid transparent",
              backgroundClip: "content-box",
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "rgba(163, 174, 208, 0.62)",
            },
          }}
        >
          {auditSummaryQuery.isLoading ? (
            <Box
              sx={{
                minHeight: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : auditSummaryQuery.isError ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 1.5 }}>
              Unable to load audit summaries.
            </Typography>
          ) : (
            <Box sx={{ display: "grid", minWidth: 0 }}>
              {summaryCard ? (
                <RangeSummaryCard
                  key={summaryCard.id}
                  card={summaryCard}
                  active={summaryCard.id === selectedReportDate}
                  onSelect={onSelectReportDate}
                />
              ) : null}

              <AuditDailyAuditGrid
                storeId={storeId}
                days={visibleDailyAuditRows}
                selectedReportDate={selectedReportDate}
                sortDirection={sortDirection}
                onSortDirectionChange={setSortDirection}
                onSelectReportDate={onSelectReportDate}
                hasPosType={hasPosType}
                isLottoAuthorized={isLottoAuthorized}
                auditFilterOptions={auditFilterOptions}
                filterFormMethods={filterFormMethods}
                onAuditFilterChange={setActiveAuditFilter}
                scratcherAuditWindows={scratcherAuditWindowsQuery.data ?? null}
                backstockLedger={backstockLedgerQuery.data ?? null}
                manualSoldOutReportsByDate={manualSoldOutReportsByDate}
                manualSoldOutImpactsByDate={manualSoldOutImpactsByDate}
                onVisibleReportDatesChange={handleVisibleReportDatesChange}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
